import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'
import type {
  SchedulingProfile,
  SchedulingProfileInput,
} from '@/types/appointments'

export const schedulingProfilesKeys = {
  list: (orgId: string | null) =>
    ['scheduling-profiles', 'list', orgId] as const,
  detail: (orgId: string | null, id: string | null) =>
    ['scheduling-profiles', 'detail', orgId, id] as const,
}

export function useSchedulingProfiles(orgId: string | null, enabled = true) {
  return useQuery<SchedulingProfile[]>({
    queryKey: schedulingProfilesKeys.list(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<SchedulingProfile>>(
        `/orgs/${orgId}/scheduling-profiles`
      )
      return res.data.data
    },
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  })
}

export function useCreateSchedulingProfile(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SchedulingProfileInput) => {
      const res = await api.post<SchedulingProfile>(
        `/orgs/${orgId}/scheduling-profiles`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduling-profiles'] })
    },
  })
}

export function useUpdateSchedulingProfile(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<SchedulingProfileInput>
    }) => {
      const res = await api.put<SchedulingProfile>(
        `/orgs/${orgId}/scheduling-profiles/${id}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduling-profiles'] })
    },
  })
}

export function useDeleteSchedulingProfile(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${orgId}/scheduling-profiles/${id}`)
      return id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scheduling-profiles'] })
    },
  })
}

export type SchedulingProfileBlock = {
  id: string
  profileId: string
  label: string | null
  startsAt: string
  endsAt: string
  createdAt: string
}

export function useSchedulingProfileBlocks(
  orgId: string | null,
  profileId: string | null
) {
  return useQuery<SchedulingProfileBlock[]>({
    queryKey: ['scheduling-profile-blocks', orgId, profileId] as const,
    queryFn: async () => {
      const res = await api.get<SchedulingProfileBlock[]>(
        `/orgs/${orgId}/scheduling-profiles/${profileId}/blocks`
      )
      return res.data
    },
    enabled: !!orgId && !!profileId,
  })
}

export function useCreateSchedulingProfileBlock(
  orgId: string | null,
  profileId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      label?: string
      startsAt: string
      endsAt: string
    }) => {
      const res = await api.post<SchedulingProfileBlock>(
        `/orgs/${orgId}/scheduling-profiles/${profileId}/blocks`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['scheduling-profile-blocks', orgId, profileId],
      })
    },
  })
}

export function useDeleteSchedulingProfileBlock(
  orgId: string | null,
  profileId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (blockId: string) => {
      await api.delete(
        `/orgs/${orgId}/scheduling-profiles/${profileId}/blocks/${blockId}`
      )
      return blockId
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['scheduling-profile-blocks', orgId, profileId],
      })
    },
  })
}
